import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Loader2, FileCheck, Calendar, DollarSign, ExternalLink } from 'lucide-react';
import { apiFetch } from '../../../utils/api';

const ContractsManagement: React.FC = () => {
    const [contracts, setContracts] = useState<any[]>([]);
    const [accounts, setAccounts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const [newContract, setNewContract] = useState({
        account_id: '', project_id: '', contract_number: `CTR-${Date.now().toString().slice(-6)}`,
        title: '', start_date: '', end_date: '', total_value: 0, file_url: ''
    });


    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [contractsRes, accsRes] = await Promise.all([
                apiFetch('/sales/contracts'),
                apiFetch('/accounts')
            ]);

            if (contractsRes.ok) setContracts(await contractsRes.json());
            if (accsRes.ok) setAccounts(await accsRes.json());
        } catch (err) { console.error(err); } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await apiFetch('/sales/contracts', {
                method: 'POST',
                body: JSON.stringify(newContract)
            });
            if (res.ok) {
                setShowCreateModal(false);
                setNewContract({
                    account_id: '', project_id: '', contract_number: `CTR-${Date.now().toString().slice(-6)}`,
                    title: '', start_date: '', end_date: '', total_value: 0, file_url: ''
                });
                fetchData();
            }
        } catch (err) { console.error(err); }
    };

    if (loading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin text-blue-500" size={40} /></div>;

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Contracts & MSA</h2>
                    <p className="text-gray-500">Legal agreements and commercial contracts</p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-xl hover:bg-indigo-700 transition font-bold shadow-lg shadow-indigo-200"
                >
                    <Plus size={20} /> Register Contract
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {contracts.map((c, i) => (
                    <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition group">
                        <div className="flex justify-between items-start mb-4">
                            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl group-hover:bg-indigo-600 group-hover:text-white transition">
                                <FileCheck size={24} />
                            </div>
                            <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${c.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                {c.status}
                            </span>
                        </div>
                        <h4 className="text-xs font-bold text-indigo-500 mb-1">{c.contract_number}</h4>
                        <h3 className="font-bold text-gray-800 text-lg mb-2">{c.title}</h3>
                        <p className="text-sm text-gray-400 mb-6">{accounts.find(a => a.id === c.account_id)?.name}</p>

                        <div className="space-y-3 pt-4 border-t border-gray-50">
                            <div className="flex items-center justify-between text-xs text-gray-500">
                                <span className="flex items-center gap-1"><Calendar size={14} /> Start</span>
                                <span className="font-bold text-gray-800">{new Date(c.start_date).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-500">
                                <span className="flex items-center gap-1"><Calendar size={14} /> End</span>
                                <span className="font-bold text-gray-800">{new Date(c.end_date).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center justify-between text-xs text-indigo-600">
                                <span className="flex items-center gap-1 font-bold"><DollarSign size={14} /> Value</span>
                                <span className="font-bold">${parseFloat(c.total_value).toLocaleString()}</span>
                            </div>
                        </div>

                        {c.file_url && (
                            <a href={c.file_url} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 mt-6 w-full py-2 bg-gray-50 text-gray-600 rounded-lg text-xs font-bold hover:bg-gray-100 transition">
                                <ExternalLink size={14} /> View Contract
                            </a>
                        )}
                    </div>
                ))}
            </div>

            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-3xl p-8 max-w-2xl w-full">
                        <h3 className="text-2xl font-bold mb-6">Register Contract</h3>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Account *</label>
                                    <select className="w-full border p-3 rounded-xl" value={newContract.account_id} onChange={e => setNewContract({ ...newContract, account_id: e.target.value })} required>
                                        <option value="">Select Account</option>
                                        {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                                    </select>
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Contract Title *</label>
                                    <input className="w-full border p-3 rounded-xl" value={newContract.title} onChange={e => setNewContract({ ...newContract, title: e.target.value })} required />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Start Date *</label>
                                    <input type="date" className="w-full border p-3 rounded-xl" value={newContract.start_date} onChange={e => setNewContract({ ...newContract, start_date: e.target.value })} required />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">End Date *</label>
                                    <input type="date" className="w-full border p-3 rounded-xl" value={newContract.end_date} onChange={e => setNewContract({ ...newContract, end_date: e.target.value })} required />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Total Value ($)</label>
                                    <input type="number" className="w-full border p-3 rounded-xl" value={newContract.total_value} onChange={e => setNewContract({ ...newContract, total_value: parseFloat(e.target.value) })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">File URL / Link</label>
                                    <input className="w-full border p-3 rounded-xl" value={newContract.file_url} onChange={e => setNewContract({ ...newContract, file_url: e.target.value })} placeholder="https://..." />
                                </div>
                            </div>
                            <div className="flex justify-end gap-4 pt-4">
                                <button type="button" onClick={() => setShowCreateModal(false)} className="px-6 py-2 text-gray-500 font-bold">Cancel</button>
                                <button type="submit" className="px-8 py-2 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700">Save Contract</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContractsManagement;
